<template>
     <div ref="timeline" class="guide-timeline__marks" 
        :style="{'--progress-timeline': progressToPx, '--width-timeline': viewportTimeDurationToPx}"
    > 
        <div 
            v-for="(mark, index) of timeMarks" :key="index"
            class="guide-timeline__mark" 
            :style="{'--width-mark': timePerPixelStyle}"
        >
            {{ mark }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import { TimeUtils} from '@/utils';
    import { useApplicationStore } from "@/stores/Application";
    import type { ProgramGuideItem } from "@ca.vmedia.tv/domains";
    import { computed, watch } from "vue";
    import { useGuideStore } from "@/stores/Guide";
    import { storeToRefs } from 'pinia';
    import { useI18n } from "vue-i18n";

    const ApplicationStore = useApplicationStore();
    const {
        getCurrentTime: currentTime,
    } = storeToRefs(ApplicationStore);

    const GuideStoreModule = useGuideStore();
    const {
        setCoordProgram,
        setViewportBeginTime,
    } = GuideStoreModule;
    const {
        timeMsPerPixel: timePerPixel,
        timelineBarTimes: barTimes,
        channelsViewPort: guideGrid,
        configProgram: getCurrentConfigProgram,
        viewportTotalTime: viewportTimeDuration,
        timeMarkInterval,
    } = storeToRefs(GuideStoreModule)

    const { locale } = useI18n();
    const {isTimeFormat12} = useAppConfig();
    
    const firstMarkbeginTime = computed((): number => {
        const residue = currentTime.value % timeMarkInterval.value;
        return currentTime.value - residue;
    });

    const timeMarks = computed((): string[] => {
        const timeFormatted: string[] = [];

        barTimes.value.forEach(timeMark => {
            const timeFormat = isTimeFormat12 ? 'hh:mm a' : 'HH:mm';
            const text: string =  TimeUtils.getMoment(locale.value, timeMark).format(timeFormat).toUpperCase();
            
            timeFormatted.push(text);
        });

        return timeFormatted;
    });
    const timePerPixelStyle = computed((): string => `${timeMarkInterval.value / timePerPixel.value}px`);
    const progressToPx = computed((): string => {
        let duration = currentTime.value - barTimes.value[0];
        duration = Math.min(Math.max(0, duration), viewportTimeDuration.value);

        return `${Math.floor(duration / timePerPixel.value)}px`;
    });
    const viewportTimeDurationToPx = computed((): string => `${Math.floor(viewportTimeDuration.value / timePerPixel.value)}px`);


    const getLiveProgram = (): number[] | null => {
        if (guideGrid.value === null || guideGrid.value.length === 0) {
            return null;
        }
        let [channelIdx] = getCurrentConfigProgram.value;
        const startime = currentTime.value;

        if (!guideGrid.value[channelIdx] || !guideGrid.value[channelIdx].items) {
            console.error('getLiveProgram - ','channelIdx:', channelIdx, 'guideGrid.value.length:', guideGrid.value.length, 'currentTime.value:', startime);
            channelIdx = 0;
        }

        const programs = [...guideGrid.value[channelIdx].items];
        const checkIsLive = (program: ProgramGuideItem, time: number)=> {
            const starTime = program.recentAirTime?.startTime ? program.recentAirTime?.startTime : 0;
            const duration = program.lengthInS ? program.lengthInS * 1000 : 0;
            const endTime = starTime + duration;

            return time >= starTime && time < endTime;
        };

        let targetProgram = programs.find((program) => checkIsLive(program, startime));

        if (targetProgram === undefined) {
            targetProgram = programs[programs.length - 1];
        }
        const newStartime = targetProgram && targetProgram.recentAirTime && targetProgram.recentAirTime?.startTime ? targetProgram.recentAirTime?.startTime : startime;
        return [channelIdx, newStartime];
    };
    const autoUpdateTimeSegment = (nextMarkTime: number) => {
        const targetConfig = getLiveProgram();

        if (targetConfig !== null) {
            setCoordProgram(targetConfig);
        }
        setViewportBeginTime(nextMarkTime);
    };
    

    watch(
        firstMarkbeginTime,
        (nextMarkTime: number) => {
            autoUpdateTimeSegment(nextMarkTime);
        }
    );
</script>
